<template>
  <div class="summary-tab">
    <h4>{{ $t("works.parcel_management.reconciliation_title") }}</h4>
    <table class="reconciliation-table">
      <thead>
        <tr>
          <th>{{ $t("works.parcel_management.col_data") }}</th>
          <th>{{ $t("works.parcel_management.reconciliation_original") }}</th>
          <th>{{ $t("works.parcel_management.reconciliation_new") }}</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in reconciliationRows" :key="row.key">
          <td>{{ row.label }}</td>
          <td>{{ formatNumber(row.original) }}</td>
          <td>{{ formatNumber(row.redistributed) }}</td>
          <td>
            <span v-if="row.matches" class="check-ok">&#10003;</span>
            <span v-else class="check-fail">&#10007;</span>
          </td>
        </tr>
      </tbody>
    </table>

    <div v-if="!reconciliationMatches" class="reconciliation-error">
      {{ $t("works.parcel_management.reconciliation_mismatch_warning") }}
    </div>
    <div v-if="hasMissingTargetParcels" class="reconciliation-error">
      {{ $t("works.parcel_management.error_missing_target_parcel") }}
    </div>

    <div class="operation-summary">
      <p>
        {{ $t("works.parcel_management.summary_operation_type") }}: <strong>{{ formData.operation_type }}</strong>
      </p>
      <p>
        {{ $t("works.parcel_management.summary_source_parcels") }}:
        <strong>{{ (formData.source_parcels || []).map((p) => p.name).join(", ") }}</strong>
      </p>
      <p>
        {{ $t("works.parcel_management.summary_target_parcels") }}:
        <strong>{{ (formData.target_parcels || []).map((p) => p.name).join(", ") }}</strong>
      </p>
    </div>

    <div v-if="isRunning" class="progress-container">
      <div class="progress-bar-outer">
        <div class="progress-bar-inner" :style="{ width: progressPercent + '%' }"></div>
      </div>
      <span>{{ $t("works.parcel_management.operation_in_progress") }}</span>
    </div>

    <SmagriaButton
      :label="$t('works.parcel_management.start_operation')"
      custom-class="primary"
      :disabled="isRunning || !canStartOperation"
      :action="startOperation"
    />
  </div>
</template>

<script>
import axios from "@/plugins/axios";
import * as endpoints from "@/store/api/endpoints";
import { successMessage, errorMessage, handlePromiseException, confirmDialog } from "@/helpers/common";
import SmagriaButton from "@/components/Common/SmagriaButton.vue";

export default {
  name: "ParcelManagementSummary",
  components: {
    SmagriaButton,
  },
  props: {
    formData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isRunning: false,
      progressPercent: 0,
      progressTimer: null,
    };
  },
  computed: {
    reconciliationRows() {
      const rows = [];

      if (this.formData.data_types.WORKS) {
        rows.push(this.buildReconciliationRow("works_surface", this.$t("works.parcel_management.col_worked_area"), "net_surface_worked"));
        rows.push(this.buildReconciliationRow("works_duration", this.$t("works.parcel_management.col_duration"), "duration_hours"));
      }

      if (this.formData.data_types.CONSUM) {
        rows.push(this.buildReconciliationRow("consum_quantity", this.$t("works.parcel_management.col_quantity") + " (" + this.$t("works.parcel_management.step_consum") + ")", "quantity", "consum_rows"));
        rows.push(this.buildReconciliationRow("consum_cost", this.$t("works.parcel_management.col_material_cost"), "material_cost", "consum_rows"));
      }

      if (this.formData.data_types.PRODUCTION) {
        rows.push(this.buildReconciliationRow("production_quantity", this.$t("works.parcel_management.col_quantity") + " (" + this.$t("works.parcel_management.step_production") + ")", "quantity", "production_rows"));
      }

      return rows;
    },
    reconciliationMatches() {
      return this.reconciliationRows.every((r) => r.matches);
    },
    hasMissingTargetParcels() {
      const rowsFieldsByType = { WORKS: "works_rows", CONSUM: "consum_rows", PRODUCTION: "production_rows" };
      return Object.keys(rowsFieldsByType).some((type) => {
        if (!this.formData.data_types[type]) {
          return false;
        }
        const rowsField = rowsFieldsByType[type];
        const selectedRowsField = rowsField.replace("_rows", "_selected_row_uuids");
        const selectedRowSet = new Set(this.formData[selectedRowsField] || []);
        return (this.formData[rowsField] || [])
          .filter((row) => selectedRowSet.has(row.uuid))
          .some((row) => !Array.isArray(row.target_parcel_uuids) || row.target_parcel_uuids.length === 0);
      });
    },
    canStartOperation() {
      return this.reconciliationMatches && !this.hasMissingTargetParcels;
    },
  },
  methods: {
    buildReconciliationRow(key, label, field, rowsField) {
      const sourceRowsField = rowsField || (key.startsWith("works") ? "works_rows" : "");
      const selectedRowsField = sourceRowsField.replace("_rows", "_selected_row_uuids");
      const selectedRowSet = new Set(this.formData[selectedRowsField] || []);
      const allRows = this.formData[sourceRowsField] || [];
      const selectedRows = allRows.filter((row) => selectedRowSet.has(row.uuid) && Array.isArray(row.target_parcel_uuids) && row.target_parcel_uuids.length > 0);
      const originalTotal = allRows.reduce((sum, row) => sum + (parseFloat(row[field]) || 0), 0);
      const redistributedTotal = selectedRows.reduce((sum, row) => sum + (parseFloat(row[field]) || 0), 0);
      const original = Math.round(originalTotal * 100) / 100;
      const redistributed = Math.round(redistributedTotal * 100) / 100;

      return {
        key,
        label,
        original,
        redistributed,
        matches: Math.abs(original - redistributed) < 0.01,
      };
    },
    formatNumber(value) {
      return Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    buildAssignments(rowsField) {
      const selectedRowsField = rowsField.replace("_rows", "_selected_row_uuids");
      const selectedRows = this.formData[selectedRowsField] || [];
      const selectedRowSet = new Set(selectedRows);
      return (this.formData[rowsField] || [])
        .filter((row) => selectedRowSet.has(row.uuid))
        .filter((row) => Array.isArray(row.target_parcel_uuids) && row.target_parcel_uuids.length > 0)
        .map((row) => ({ uuid: row.uuid, row_type: row.row_type, target_parcel_uuids: row.target_parcel_uuids }));
    },
    buildReconciliationPayload() {
      const original = {};
      const redistributed = {};
      this.reconciliationRows.forEach((row) => {
        original[row.key] = row.original;
        redistributed[row.key] = row.redistributed;
      });
      return { original, redistributed };
    },
    startProgressSimulation() {
      this.progressPercent = 0;
      this.progressTimer = setInterval(() => {
        if (this.progressPercent < 90) {
          this.progressPercent += Math.random() * 10;
        }
      }, 300);
    },
    stopProgressSimulation() {
      if (this.progressTimer) {
        clearInterval(this.progressTimer);
        this.progressTimer = null;
      }
      this.progressPercent = 100;
    },
    async startOperation() {
      if (!this.canStartOperation) {
        errorMessage(
          this,
          this.$t(this.hasMissingTargetParcels ? "works.parcel_management.error_missing_target_parcel" : "works.parcel_management.reconciliation_mismatch_warning")
        );
        return;
      }

      try {
        await confirmDialog(
          this.$t("works.parcel_management.confirm_start_operation_message"),
          this.$t("works.parcel_management.confirm_start_operation_title"),
          {
            okText: this.$t("ok"),
            cancelText: this.$t("common.cancel"),
          }
        );
      } catch (e) {
        return;
      }

      this.isRunning = true;
      this.startProgressSimulation();

      const payload = {
        operation_type: this.formData.operation_type,
        source_parcel_uuids: (this.formData.source_parcels || []).map((p) => p.uuid),
        target_parcel_uuids: (this.formData.target_parcels || []).map((p) => p.uuid),
        data_types: Object.keys(this.formData.data_types).filter((key) => this.formData.data_types[key]),
        assignments: {
          works: this.formData.data_types.WORKS ? this.buildAssignments("works_rows") : [],
          consum: this.formData.data_types.CONSUM ? this.buildAssignments("consum_rows") : [],
          production: this.formData.data_types.PRODUCTION ? this.buildAssignments("production_rows") : [],
        },
        reconciliation: this.buildReconciliationPayload(),
      };

      try {
        const response = await axios.post(endpoints.PARCEL_MANAGEMENT_EXECUTE, payload);
        this.stopProgressSimulation();
        successMessage(this, response?.data?.message);
        this.$emit("executed");
      } catch (e) {
        this.stopProgressSimulation();
        handlePromiseException(this, e);
      } finally {
        this.isRunning = false;
      }
    },
  },
  beforeDestroy() {
    this.stopProgressSimulation();
  },
};
</script>

<style scoped>
.reconciliation-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 16px;
}

.reconciliation-table th,
.reconciliation-table td {
  border: 1px solid #e0e0e0;
  padding: 8px 12px;
  text-align: left;
}

.check-ok {
  color: #2e7d32;
  font-weight: bold;
}

.check-fail {
  color: #c62828;
  font-weight: bold;
}

.reconciliation-error {
  color: #c62828;
  margin-bottom: 16px;
}

.operation-summary {
  margin-bottom: 16px;
}

.progress-container {
  margin: 16px 0;
}

.progress-bar-outer {
  width: 100%;
  height: 10px;
  background-color: #eee;
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 6px;
}

.progress-bar-inner {
  height: 100%;
  background-color: #2e7d32;
  transition: width 0.3s ease;
}
</style>
